import { useState } from "react";
import {
  KeyRound,
  Laptop,
  Loader2,
  MonitorSmartphone,
  ShieldCheck,
  ShieldQuestion,
  Smartphone,
} from "lucide-react";
import { useDecideDeviceApproval, useDeviceApprovals } from "@empite/session";
import type { DeviceApproval } from "@empite/api-client";
import { AppIcon } from "../ui/app-icon";
import { Button } from "../ui/button";
import { Card, CardBody, CardFooter, CardHeader } from "../ui/card";
import { EmptyState } from "../ui/empty-state";
import { formatDateTime, humanizeState, relativeTime, truncateId } from "../ui/format";
import { Notice } from "../ui/notice";
import { PageHeader } from "../ui/page-header";
import { Skeleton } from "../ui/skeleton";
import { StatusPill } from "../ui/status-pill";
import { errorMessage, RequireSession } from "./guards";

/** Risk is the server's word, and the colour follows it rather than restating
 *  it. An unrecognised level reads neutral rather than reassuring: the pill
 *  says what the platform said, and says nothing more. */
function riskTone(risk: string): "danger" | "warning" | "neutral" {
  switch (risk) {
    case "high":
      return "danger";
    case "elevated":
    case "medium":
      return "warning";
    default:
      return "neutral";
  }
}

/** The glyph a device gets. Only the device NAME is available here — unlike
 *  `/security`, an approval carries no platform string — so the match is on
 *  whatever the installing client called itself, and anything unrecognised
 *  falls back to the neutral icon rather than guessing. */
function deviceIcon(name: string | undefined) {
  const value = (name ?? "").toLowerCase();
  if (/iphone|ipad|android|phone|mobile|pixel/.test(value)) {
    return <Smartphone className="size-4" aria-hidden />;
  }
  if (/mac|book|windows|linux|desktop|pc/.test(value)) {
    return <Laptop className="size-4" aria-hidden />;
  }
  return <MonitorSmartphone className="size-4" aria-hidden />;
}

/**
 * The device-approval screen.
 *
 * Approving only stamps the row; DENYING revokes the installation's binding,
 * its sessions and its refresh families. That asymmetry is why the deny
 * button says what it does — a customer clicking it is ending an
 * installation, not dismissing a notification.
 */
export function DeviceApprovals() {
  return <RequireSession>{() => <ApprovalList />}</RequireSession>;
}

function ApprovalList() {
  const approvals = useDeviceApprovals();
  const decide = useDecideDeviceApproval();
  const [decided, setDecided] = useState<string | null>(null);

  const rows = approvals.data?.device_approvals ?? [];
  const pending = rows.filter((row) => row.status === "pending");
  const settled = rows.filter((row) => row.status !== "pending");

  return (
    <>
      <PageHeader
        title="Device approvals"
        description="A new installation of a suite application asked to join your account. Nothing it asked for is granted until you say so."
        eyebrow={
          pending.length > 0 ? (
            <StatusPill tone="warning" dot pulse>
              {pending.length} waiting for you
            </StatusPill>
          ) : null
        }
        actions={
          <Button href="/security" variant="secondary">
            Profile &amp; security
          </Button>
        }
      />

      {/* The decision banners are the only feedback a keyboard or screen-reader
          user gets that the button did anything — the row it was in disappears
          on the refetch. The ported Notice is presentation only, so the live
          region is the caller's: polite for a confirmation, assertive for a
          decision that did not go through. */}
      <div aria-live="polite">
        {decided ? (
          <Notice
            tone="success"
            title="Decision recorded"
            icon={<ShieldCheck className="size-4" aria-hidden />}
            className="mb-6"
          >
            {decided}
          </Notice>
        ) : null}
      </div>
      <div aria-live="assertive">
        {decide.error ? (
          <Notice tone="danger" title="That decision did not go through" className="mb-6">
            {errorMessage(decide.error)}
          </Notice>
        ) : null}
      </div>

      {approvals.isPending ? (
        <div className="space-y-5">
          <Skeleton className="h-52 w-full rounded-card" />
          <Skeleton className="h-52 w-full rounded-card" />
        </div>
      ) : approvals.error ? (
        <Notice tone="danger" title="Could not load your device approvals">
          {errorMessage(approvals.error)}
        </Notice>
      ) : (
        <div className="space-y-5">
          {pending.length === 0 ? (
            <Card>
              <EmptyState
                icon={<ShieldCheck className="size-7" />}
                title="Nothing is waiting for you"
                body="New installations of an application you already use will appear here before they can sign in."
                hint="You will also see a badge in the sidebar when one arrives."
                action={
                  <Button href="/security" variant="secondary" size="sm">
                    Review your devices
                  </Button>
                }
              />
            </Card>
          ) : (
            pending.map((approval) => (
              <PendingApproval
                key={approval.approval_id}
                approval={approval}
                decide={decide}
                onDecided={setDecided}
              />
            ))
          )}

          {settled.length > 0 ? <SettledCard approvals={settled} /> : null}
        </div>
      )}
    </>
  );
}

function PendingApproval({
  approval,
  decide,
  onDecided,
}: {
  approval: DeviceApproval;
  decide: ReturnType<typeof useDecideDeviceApproval>;
  onDecided: (message: string) => void;
}) {
  const busy = decide.isPending && decide.variables?.approvalID === approval.approval_id;
  const device = approval.device_name ?? "Unnamed device";
  const expired = Date.parse(approval.expires_at) < Date.now();

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title={
          <span className="flex items-center gap-3">
            <AppIcon initial={(approval.application_name.trim()[0] ?? "?").toUpperCase()} size="sm" />
            {approval.application_name}
          </span>
        }
        description={`Asked ${relativeTime(approval.created_at)} · ${formatDateTime(approval.created_at)}`}
        action={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <StatusPill tone={riskTone(approval.risk_level)} dot>
              {humanizeState(approval.risk_level)} risk
            </StatusPill>
            {expired ? <StatusPill tone="neutral">expired</StatusPill> : null}
          </div>
        }
      />

      <CardBody className="space-y-4">
        <div className="flex flex-wrap items-start gap-4">
          <span
            aria-hidden
            className="grid size-9 shrink-0 place-items-center rounded-full bg-brand-softer text-brand ring-1 ring-brand/10"
          >
            {deviceIcon(approval.device_name)}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold text-ink">{device}</p>
            <p className="mt-1 text-2xs text-ink-4">
              {approval.installation_id ? (
                <>
                  Installation{" "}
                  <code className="font-mono">{truncateId(approval.installation_id)}</code> ·{" "}
                </>
              ) : null}
              {expired ? "Expired" : "Expires"} {formatDateTime(approval.expires_at)}
            </p>
          </div>
        </div>

        <div className="rounded-card border border-line-soft bg-surface-sunken/40 p-4">
          <p className="flex items-center gap-2 text-2xs font-bold tracking-[0.07em] text-ink-4 uppercase">
            <KeyRound className="size-3.5" aria-hidden />
            Requested permissions
          </p>
          {approval.requested_scopes.length === 0 ? (
            <p className="mt-3 text-sm text-ink-4">It asked for nothing beyond signing in.</p>
          ) : (
            <ul className="mt-3 flex flex-wrap gap-1.5">
              {approval.requested_scopes.map((scope) => (
                <li
                  key={scope}
                  className="rounded-field bg-surface px-2 py-1 font-mono text-2xs text-ink-2 ring-1 ring-line-soft"
                >
                  {scope}
                </li>
              ))}
            </ul>
          )}
        </div>

        {riskTone(approval.risk_level) !== "neutral" ? (
          <Notice
            tone={riskTone(approval.risk_level) === "danger" ? "danger" : "warning"}
            title="This request did not look like your usual sign-in"
            icon={<ShieldQuestion className="size-4" aria-hidden />}
          >
            If you did not just install {approval.application_name} on {device}, deny it — that
            ends the installation's access rather than only dismissing this request.
          </Notice>
        ) : null}
      </CardBody>

      <CardFooter className="flex flex-wrap items-center justify-end gap-2">
        <Button
          variant="danger"
          disabled={busy}
          onClick={async () => {
            await decide.mutateAsync({ approvalID: approval.approval_id, approve: false });
            onDecided(`Denied ${device} — its access has been revoked.`);
          }}
        >
          {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
          Deny and revoke
        </Button>
        <Button
          disabled={busy}
          onClick={async () => {
            await decide.mutateAsync({ approvalID: approval.approval_id, approve: true });
            onDecided(`Approved ${device}.`);
          }}
        >
          {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
          Approve
        </Button>
      </CardFooter>
    </Card>
  );
}

/** Decided requests stay on screen: a list that drops what it decided gives
 *  the customer no way to confirm which way it went. */
function SettledCard({ approvals }: { approvals: DeviceApproval[] }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Already decided"
        description="Requests you have answered, and the ones that timed out."
        action={<span className="text-xs text-ink-4">{approvals.length}</span>}
      />
      <ul className="divide-y divide-line-soft">
        {approvals.map((approval) => (
          <li
            key={approval.approval_id}
            className="flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-4 sm:px-6"
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate font-semibold text-ink">
                {approval.application_name}
              </span>
              <span className="mt-0.5 block text-2xs text-ink-4">
                {approval.device_name ?? "Unnamed device"} ·{" "}
                {approval.decided_at
                  ? `decided ${relativeTime(approval.decided_at)}`
                  : `asked ${relativeTime(approval.created_at)}`}
              </span>
            </span>
            <StatusPill tone={riskTone(approval.risk_level)}>
              {humanizeState(approval.risk_level)} risk
            </StatusPill>
            <StatusPill tone={approval.status === "approved" ? "success" : "danger"} dot>
              {humanizeState(approval.status)}
            </StatusPill>
          </li>
        ))}
      </ul>
    </Card>
  );
}
